import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none'
  }
}));

const ClickableTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px'
  }
}));

const ACTOR_META = {
  user: { label: 'BowlScout user', color: 'primary' },
  portal_personal: { label: 'Portal contact', color: 'success' },
  portal_shared: { label: 'Shared portal PIN', color: 'warning' },
  anonymous: { label: 'Unknown device', color: 'default' }
};

export const SOURCE_LABEL = {
  organiser: 'Organiser',
  portal: 'Portal',
  client: 'Client',
  system: 'System'
};

export const actorLabel = (actor) =>
  actor?.name || ACTOR_META[actor?.type]?.label || 'Unknown';

export const formatAuditTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const ActorCell = ({ actor }) => {
  const meta = ACTOR_META[actor?.type] || ACTOR_META.anonymous;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" fontWeight={600}>
        {actorLabel(actor)}
      </Typography>
      {actor?.email && (
        <Typography variant="caption" color="text.secondary">
          {actor.email}
        </Typography>
      )}
      <Chip
        size="small"
        variant="outlined"
        color={meta.color}
        label={meta.label}
        sx={{ alignSelf: 'flex-start' }}
      />
    </Stack>
  );
};

const portalLabel = (scope) => {
  if (scope?.venueName) return scope.venueName;
  if (scope?.kind === 'secretary') return 'Tournament Secretary';
  if (scope?.kind === 'club') return 'Club portal';
  return 'This event';
};

const AuditTable = ({ items, view, onSelect }) => {
  const isAccess = view === 'access';

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={isAccess ? 'Portal access records' : 'Event changes'}>
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ minWidth: 160 }}>When</StyledTableCell>
            <StyledTableCell sx={{ minWidth: 220 }}>Action</StyledTableCell>
            <StyledTableCell sx={{ minWidth: 200 }}>Actor</StyledTableCell>
            {isAccess ? (
              <StyledTableCell align="center" sx={{ minWidth: 100 }}>
                Outcome
              </StyledTableCell>
            ) : (
              <StyledTableCell sx={{ minWidth: 200 }}>Changed item</StyledTableCell>
            )}
            <StyledTableCell sx={{ minWidth: 160 }}>Portal</StyledTableCell>
            {isAccess ? (
              <StyledTableCell sx={{ minWidth: 220 }}>Browser / device</StyledTableCell>
            ) : (
              <StyledTableCell sx={{ minWidth: 110 }}>Source</StyledTableCell>
            )}
            <StyledTableCell
              align="center"
              sx={{ width: 56 }}
              aria-label="Record details"
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <ClickableTableRow
              key={item.id}
              hover
              tabIndex={0}
              onClick={() => onSelect(item)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(item);
                }
              }}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatAuditTime(item.createdAt)}
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {item.actionLabel || item.action}
                </Typography>
                {item.summary && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.summary}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <ActorCell actor={item.actor} />
              </TableCell>
              {isAccess ? (
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={item.outcome === 'failure' ? 'error' : 'success'}
                    variant="outlined"
                    label={item.outcome === 'failure' ? 'Failed' : 'Success'}
                  />
                </TableCell>
              ) : (
                <TableCell>
                  <Typography variant="body2">
                    {item.entity?.name || item.entity?.id || '—'}
                  </Typography>
                  {item.entity?.type && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.entity.type}
                    </Typography>
                  )}
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body2">{portalLabel(item.scope)}</Typography>
              </TableCell>
              {isAccess ? (
                <TableCell>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.ip || 'No IP recorded'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ wordBreak: 'break-word' }}>
                    {item.userAgent || 'No device recorded'}
                  </Typography>
                </TableCell>
              ) : (
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={SOURCE_LABEL[item.source] || item.source || 'Unknown'}
                  />
                </TableCell>
              )}
              <TableCell align="center">
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    aria-label="View audit record details"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(item);
                    }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </ClickableTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AuditTable;
